/**
 * Premade bot trainer presets with fixed teams and sprite references.
 * Bot sprite files remain user-provided under public/assets/sprites/.
 * Teams are stored in the same Showdown text format players can export/import.
 */

import type { TrainerProfile, PokemonSet, IBotPlayer } from "../engine/types";
import { createPremadeTeamBot } from "../engine/bots";
import { parseShowdownText } from "./showdown";

export type BotPreset = {
  id: string;
  name: string;
  description: string;
  profile: TrainerProfile;
  team: PokemonSet[];
  background: string;
  bot: IBotPlayer;
};

function makeProfile(name: string, spriteId: string): TrainerProfile {
  return {
    name,
    backSprite: spriteId,
    frontSprite: spriteId,
    vsSprite: spriteId,
    isCustom: false,
  };
}

function parseTeam(text: string): PokemonSet[] {
  return parseShowdownText(text);
}

// --- Team strings in player-exportable Showdown format ---

const CHILI_TEAM = `
Victini @ Choice Band
Ability: Victory Star
Level: 50
EVs: 252 Atk / 4 SpD / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- V-create
- Bolt Strike
- Zen Headbutt
- U-turn

Darmanitan @ Life Orb
Ability: Sheer Force
Level: 50
EVs: 252 Atk / 4 SpD / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Flare Blitz
- Rock Slide
- Earthquake
- U-turn

Chandelure @ Choice Scarf
Ability: Flash Fire
Level: 50
EVs: 252 SpA / 4 SpD / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Overheat
- Shadow Ball
- Energy Ball
- Trick

Volcarona @ Heavy-Duty Boots
Ability: Flame Body
Level: 50
EVs: 252 SpA / 4 SpD / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Quiver Dance
- Fiery Dance
- Giga Drain
- Bug Buzz

Infernape @ Focus Sash
Ability: Iron Fist
Level: 50
EVs: 252 Atk / 4 SpD / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Close Combat
- Flare Blitz
- Mach Punch
- Thunder Punch

Ninetales @ Heat Rock
Ability: Drought
Level: 50
EVs: 248 HP / 8 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Solar Beam
- Flamethrower
- Will-O-Wisp
- Roar
`;

const CILAN_TEAM = `
Serperior @ Leftovers
Ability: Contrary
Level: 50
EVs: 248 HP / 8 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Leaf Storm
- Dragon Pulse
- Substitute
- Leech Seed

Ferrothorn @ Rocky Helmet
Ability: Iron Barbs
Level: 50
EVs: 252 HP / 48 Def / 208 SpD
Relaxed Nature
IVs: 31 HP / 31 Atk / 31 Def / 0 SpA / 31 SpD / 0 Spe
- Power Whip
- Gyro Ball
- Stealth Rock
- Spikes

Breloom @ Focus Sash
Ability: Technician
Level: 50
EVs: 252 Atk / 4 SpD / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Spore
- Bullet Seed
- Mach Punch
- Rock Tomb

Rotom-Mow @ Choice Scarf
Ability: Levitate
Level: 50
EVs: 252 SpA / 4 SpD / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Leaf Storm
- Thunderbolt
- Volt Switch
- Trick

Amoonguss @ Black Sludge
Ability: Regenerator
Level: 50
EVs: 252 HP / 160 Def / 96 SpD
Bold Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Spore
- Giga Drain
- Sludge Bomb
- Clear Smog

Exeggutor @ Life Orb
Ability: Chlorophyll
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Modest Nature
IVs: 31 HP / 0 Atk / 31 Def / 30 SpA / 31 SpD / 30 Spe
- Solar Beam
- Psychic
- Hidden Power Fire
- Sleep Powder
`;

const CRESS_TEAM = `
Keldeo @ Choice Specs
Ability: Justified
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Hydro Pump
- Secret Sword
- Icy Wind
- Focus Blast

Swampert @ Leftovers
Ability: Torrent
Level: 50
EVs: 252 HP / 252 Atk / 4 Def
Adamant Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Waterfall
- Earthquake
- Stealth Rock
- Roar

Starmie @ Life Orb
Ability: Natural Cure
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Hydro Pump
- Ice Beam
- Thunderbolt
- Rapid Spin

Gyarados @ Choice Band
Ability: Intimidate
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Waterfall
- Earthquake
- Stone Edge
- Ice Fang

Toxicroak @ Focus Sash
Ability: Dry Skin
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Swords Dance
- Drain Punch
- Sucker Punch
- Ice Punch

Kingdra @ Choice Specs
Ability: Swift Swim
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Modest Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Hydro Pump
- Draco Meteor
- Ice Beam
- Rain Dance
`;

const HILBERT_TEAM = `
Emboar @ Life Orb
Ability: Blaze
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Flare Blitz
- Superpower
- Wild Charge
- Head Smash

Samurott @ Choice Scarf
Ability: Torrent
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Modest Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Hydro Pump
- Ice Beam
- Grass Knot
- Megahorn

Serperior @ Leftovers
Ability: Overgrow
Level: 50
EVs: 248 HP / 8 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Leaf Storm
- Dragon Pulse
- Substitute
- Leech Seed

Unfezant @ Choice Band
Ability: Super Luck
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Brave Bird
- Return
- U-turn
- Quick Attack

Haxorus @ Lum Berry
Ability: Mold Breaker
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Outrage
- Earthquake
- Superpower
- Dragon Dance

Chandelure @ Choice Specs
Ability: Flash Fire
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Overheat
- Shadow Ball
- Energy Ball
- Trick
`;

const HILDA_TEAM = `
Emboar @ Choice Scarf
Ability: Blaze
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Adamant Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Flare Blitz
- Superpower
- Wild Charge
- Head Smash

Samurott @ Leftovers
Ability: Torrent
Level: 50
EVs: 252 HP / 252 SpA / 4 SpD
Modest Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Hydro Pump
- Ice Beam
- Grass Knot
- Protect

Serperior @ Choice Specs
Ability: Overgrow
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Leaf Storm
- Dragon Pulse
- Hidden Power Fire
- Giga Drain

Unfezant @ Sharp Beak
Ability: Super Luck
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Brave Bird
- Return
- U-turn
- Quick Attack

Haxorus @ Focus Sash
Ability: Mold Breaker
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Outrage
- Earthquake
- Superpower
- Dragon Dance

Chandelure @ Life Orb
Ability: Flash Fire
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Flamethrower
- Shadow Ball
- Energy Ball
- Substitute
`;

const CHEREN_TEAM = `
Stoutland @ Silk Scarf
Ability: Intimidate
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Adamant Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Return
- Crunch
- Wild Charge
- Superpower

Liepard @ Focus Sash
Ability: Prankster
Level: 50
EVs: 252 HP / 4 Def / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Thunder Wave
- Swagger
- Foul Play
- Substitute

Watchog @ Lum Berry
Ability: Illuminate
Level: 50
EVs: 252 HP / 252 Atk / 4 SpD
Adamant Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Hypnosis
- Super Fang
- Return
- Low Kick

Simisage @ Life Orb
Ability: Overgrow
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Leaf Storm
- Focus Blast
- Hidden Power Ice
- Energy Ball

Simisear @ Life Orb
Ability: Blaze
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Fire Blast
- Focus Blast
- Hidden Power Grass
- Nasty Plot

Simipour @ Life Orb
Ability: Torrent
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Hydro Pump
- Ice Beam
- Hidden Power Electric
- Nasty Plot
`;

const CHEREN2_TEAM = `
Stoutland @ Choice Band
Ability: Intimidate
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Adamant Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Return
- Crunch
- Wild Charge
- Superpower

Liepard @ Focus Sash
Ability: Prankster
Level: 50
EVs: 252 HP / 4 Def / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Thunder Wave
- Swagger
- Foul Play
- Substitute

Unfezant @ Choice Band
Ability: Super Luck
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Brave Bird
- Return
- U-turn
- Quick Attack

Gigalith @ Leftovers
Ability: Sturdy
Level: 50
EVs: 252 HP / 252 Atk / 4 SpD
Adamant Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Stone Edge
- Earthquake
- Stealth Rock
- Explosion

Haxorus @ Lum Berry
Ability: Mold Breaker
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Outrage
- Earthquake
- Superpower
- Dragon Dance

Chandelure @ Choice Specs
Ability: Flash Fire
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Overheat
- Shadow Ball
- Energy Ball
- Trick
`;

const BIANCA_TEAM = `
Stoutland @ Silk Scarf
Ability: Intimidate
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Adamant Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Return
- Crunch
- Wild Charge
- Superpower

Musharna @ Leftovers
Ability: Synchronize
Level: 50
EVs: 252 HP / 252 SpA / 4 SpD
Modest Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Psychic
- Moonlight
- Calm Mind
- Shadow Ball

Simisear @ Life Orb
Ability: Blaze
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Fire Blast
- Focus Blast
- Hidden Power Grass
- Nasty Plot

Simipour @ Life Orb
Ability: Torrent
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Hydro Pump
- Ice Beam
- Hidden Power Electric
- Nasty Plot

Simisage @ Life Orb
Ability: Overgrow
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Leaf Storm
- Focus Blast
- Hidden Power Ice
- Energy Ball

Chandelure @ Choice Specs
Ability: Flash Fire
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Overheat
- Shadow Ball
- Energy Ball
- Trick
`;

const HUGH_TEAM = `
Emboar @ Choice Scarf
Ability: Blaze
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Adamant Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Flare Blitz
- Superpower
- Wild Charge
- Head Smash

Flygon @ Life Orb
Ability: Levitate
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Outrage
- Earthquake
- U-turn
- Stone Edge

Simisage @ Focus Sash
Ability: Overgrow
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Leaf Storm
- Focus Blast
- Hidden Power Ice
- Energy Ball

Eelektross @ Assault Vest
Ability: Levitate
Level: 50
EVs: 252 HP / 128 Atk / 128 SpA
Brave Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Thunderbolt
- Flamethrower
- Giga Drain
- Superpower

Darmanitan @ Choice Band
Ability: Sheer Force
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Flare Blitz
- Rock Slide
- Earthquake
- U-turn

Samurott @ Leftovers
Ability: Torrent
Level: 50
EVs: 252 HP / 252 SpA / 4 SpD
Modest Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Hydro Pump
- Ice Beam
- Grass Knot
- Protect
`;

const NATE_TEAM = `
Emboar @ Choice Scarf
Ability: Blaze
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Adamant Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Flare Blitz
- Superpower
- Wild Charge
- Head Smash

Lucario @ Life Orb
Ability: Inner Focus
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Close Combat
- Extreme Speed
- Crunch
- Swords Dance

Serperior @ Choice Specs
Ability: Overgrow
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Leaf Storm
- Dragon Pulse
- Hidden Power Fire
- Giga Drain

Stoutland @ Choice Band
Ability: Intimidate
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Adamant Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Return
- Crunch
- Wild Charge
- Superpower

Haxorus @ Lum Berry
Ability: Mold Breaker
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Outrage
- Earthquake
- Superpower
- Dragon Dance

Chandelure @ Choice Specs
Ability: Flash Fire
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Overheat
- Shadow Ball
- Energy Ball
- Trick
`;

const ROSA_TEAM = `
Serperior @ Leftovers
Ability: Overgrow
Level: 50
EVs: 248 HP / 8 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Leaf Storm
- Dragon Pulse
- Substitute
- Leech Seed

Samurott @ Life Orb
Ability: Torrent
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Modest Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Hydro Pump
- Ice Beam
- Grass Knot
- Megahorn

Emboar @ Choice Band
Ability: Blaze
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Adamant Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Flare Blitz
- Superpower
- Wild Charge
- Head Smash

Arcanine @ Leftovers
Ability: Intimidate
Level: 50
EVs: 252 HP / 252 Atk / 4 Def
Adamant Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Flare Blitz
- Wild Charge
- Morning Sun
- Extreme Speed

Lucario @ Life Orb
Ability: Inner Focus
Level: 50
EVs: 4 HP / 252 Atk / 252 Spe
Jolly Nature
IVs: 31 HP / 31 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Close Combat
- Extreme Speed
- Crunch
- Swords Dance

Chandelure @ Choice Specs
Ability: Flash Fire
Level: 50
EVs: 4 HP / 252 SpA / 252 Spe
Timid Nature
IVs: 31 HP / 0 Atk / 31 Def / 31 SpA / 31 SpD / 31 Spe
- Overheat
- Shadow Ball
- Energy Ball
- Trick
`;

/**
 * Premade bot presets matching every trainer sprite in public/assets/sprites/trainers/.
 * Each team is stored as a player-exportable Showdown text block for easy editing.
 */
export const BOT_PRESETS: BotPreset[] = [
  {
    id: "chili",
    name: "Chili (Fire)",
    description: "A fiery Striaton City gym leader who favors Fire-types.",
    profile: makeProfile("Chili", "chili"),
    team: parseTeam(CHILI_TEAM),
    background: "volcano",
    bot: null as unknown as IBotPlayer,
  },
  {
    id: "cilan",
    name: "Cilan (Grass)",
    description: "A calm Striaton City gym leader who loves Grass-types.",
    profile: makeProfile("Cilan", "cilan"),
    team: parseTeam(CILAN_TEAM),
    background: "forest",
    bot: null as unknown as IBotPlayer,
  },
  {
    id: "cress",
    name: "Cress (Water)",
    description: "A relaxed Striaton City gym leader who adores Water-types.",
    profile: makeProfile("Cress", "cress"),
    team: parseTeam(CRESS_TEAM),
    background: "water",
    bot: null as unknown as IBotPlayer,
  },
  {
    id: "hilbert",
    name: "Hilbert",
    description: "The determined Unova hero with a balanced team.",
    profile: makeProfile("Hilbert", "hilbert"),
    team: parseTeam(HILBERT_TEAM),
    background: "grass",
    bot: null as unknown as IBotPlayer,
  },
  {
    id: "hilda",
    name: "Hilda",
    description: "The energetic Unova heroine with a balanced team.",
    profile: makeProfile("Hilda", "hilda"),
    team: parseTeam(HILDA_TEAM),
    background: "grass",
    bot: null as unknown as IBotPlayer,
  },
  {
    id: "cheren",
    name: "Cheren",
    description: "A serious rival focused on Normal-types and the three monkeys.",
    profile: makeProfile("Cheren", "cheren"),
    team: parseTeam(CHEREN_TEAM),
    background: "city",
    bot: null as unknown as IBotPlayer,
  },
  {
    id: "cheren2",
    name: "Cheren (Rematch)",
    description: "A stronger Cheren with a more developed team.",
    profile: makeProfile("Cheren", "cheren2"),
    team: parseTeam(CHEREN2_TEAM),
    background: "building",
    bot: null as unknown as IBotPlayer,
  },
  {
    id: "bianca",
    name: "Bianca",
    description: "A cheerful researcher with a love for Fire, Water, and Grass.",
    profile: makeProfile("Bianca", "bianca"),
    team: parseTeam(BIANCA_TEAM),
    background: "city",
    bot: null as unknown as IBotPlayer,
  },
  {
    id: "hugh",
    name: "Hugh",
    description: "A hot-blooded rival with a powerful Emboar and Flygon.",
    profile: makeProfile("Hugh", "hugh"),
    team: parseTeam(HUGH_TEAM),
    background: "city",
    bot: null as unknown as IBotPlayer,
  },
  {
    id: "nate",
    name: "Nate",
    description: "A new Unova hero with a strong Fighting-type core.",
    profile: makeProfile("Nate", "nate"),
    team: parseTeam(NATE_TEAM),
    background: "grass",
    bot: null as unknown as IBotPlayer,
  },
  {
    id: "rosa",
    name: "Rosa",
    description: "A new Unova heroine with a balanced starter team.",
    profile: makeProfile("Rosa", "rosa"),
    team: parseTeam(ROSA_TEAM),
    background: "grass",
    bot: null as unknown as IBotPlayer,
  },
];

// Fill in bots after creation
const presetBots = BOT_PRESETS.map((p) => {
  return createPremadeTeamBot(p.id, p.name, p.profile, { id: p.id, name: p.name, format: "gen5", members: p.team, createdAt: "", updatedAt: "" });
});

// Assign bots back to presets
for (let i = 0; i < BOT_PRESETS.length; i++) {
  BOT_PRESETS[i].bot = presetBots[i];
}

/** Get a bot preset by ID. */
export function getBotPreset(id: string): BotPreset | undefined {
  return BOT_PRESETS.find((p) => p.id === id);
}

/** All premade bot player instances. */
export const PREMADE_BOTS: IBotPlayer[] = BOT_PRESETS.map((p) => p.bot);